import * as zod from 'zod';

import { convertMetersPerSecond } from '../../shared/convertMetersPerSecond';
import {
  GasType,
  GasTypeRaw,
  WireSize,
  WireSizeRaw,
  WireType,
  WireTypeRaw,
} from '../implementation/constants';

import { WeldMachineMode } from './MillerWeldMachineState';

export const MILLER_DEFAULTS = {
  acceleration: 0.05, // 50 mm/s^2
  voltage: 18, // Volts
  arcLength: 0.25, // Meters
  arcControl: 25, // Percent
  wireFeedSpeed: convertMetersPerSecond.fromInchesPerMinute(200), // 200 ipm
};

export const MILLER_WFS_BOUNDS = {
  min: convertMetersPerSecond.fromInchesPerMinute(50),
  max: convertMetersPerSecond.fromInchesPerMinute(780),
};

// Shielding gas flow durations, in seconds. Neither flow can be driven to zero
// while enabled — a flow that runs for no time is a disabled flow — so preflow
// and postflow share one range and one schema. The editor clamps into this range
// rather than rejecting out-of-range input (`NumberInput` min/max).
//
// The ceiling is an arc-start constraint rather than a machine limit. Preflow
// purges gas on the machine's own weld sequencer BEFORE the arc can establish,
// while `startArc` races `arcDetected` against `WAIT_FOR_MACHINE_TO_RESPOND_MS`
// (4s) — so a preflow near that timeout fails every weld with "No arc
// detected". Postflow shares the ceiling only because it shares the range;
// nothing about postflow needs it. Raising either means making that wait
// account for the configured preflow instead of being a fixed constant.
export const GAS_FLOW_BOUNDS = { min: 0.1, max: 3 };

// The shortest permitted flow, so enabling one commits to as little gas as the
// range allows until the operator raises it.
export const GAS_FLOW_DEFAULT_DURATION = 0.1;

const gasFlowConfig = zod.object({
  enabled: zod.boolean().default(false),
  duration: zod
    .number()
    .gte(GAS_FLOW_BOUNDS.min)
    .lte(GAS_FLOW_BOUNDS.max)
    .default(GAS_FLOW_DEFAULT_DURATION),
});

export type GasFlowConfig = zod.infer<typeof gasFlowConfig>;

/**
 * What to send for a flow the routine never configured.
 *
 * A routine saved before pre/postflow existed has no such key in its stored
 * arguments, and the routine runner hands steps their *raw* arguments rather
 * than the zod-parsed ones, so schema defaults do not apply at execution time.
 * Sending an explicit disable is also the safer reading: it clears whatever the
 * previous routine left on the machine.
 */
export const GAS_FLOW_DISABLED: GasFlowConfig = {
  enabled: false,
  duration: GAS_FLOW_DEFAULT_DURATION,
};

export const MillerWeldParameters = zod.object({
  mode: WeldMachineMode,
  wireType: WireType.default(WireTypeRaw.Aluminum_4943),
  wireSize: WireSize.default(WireSizeRaw.Size_0_030),
  gasType: GasType.default(GasTypeRaw.Argon_100),
  // Max is 38v when power source is 350A rated. See section 3.4.1.8
  voltage: zod.number().gte(10).lte(44),
  arcLength: zod.number().gte(-10).lte(10),
  arcControl: zod.number().gte(0).lte(50),
  wireFeedSpeed: zod
    .number()
    .gte(MILLER_WFS_BOUNDS.min)
    .lte(MILLER_WFS_BOUNDS.max),
  preflow: gasFlowConfig.default({}),
  postflow: gasFlowConfig.default({}),
});

export type MillerWeldParameters = zod.infer<typeof MillerWeldParameters>;

export const MillerWeldMachineCommand = zod.object({
  kind: zod.literal('MillerWeldMachineCommand'),
  deviceID: zod.string().optional(),
  subCommand: zod.discriminatedUnion('kind', [
    zod.object({
      kind: zod.literal('startArc'),
    }),
    zod.object({
      kind: zod.literal('stopArc'),
    }),
    zod.object({
      kind: zod.literal('startGas'),
    }),
    zod.object({
      kind: zod.literal('stopGas'),
    }),
    zod.object({
      kind: zod.literal('startWireFeed'),
      wireFeedSpeed: zod
        .number()
        .gte(MILLER_WFS_BOUNDS.min)
        .lte(MILLER_WFS_BOUNDS.max)
        .optional(),
      // Only include forward direction because that is what our wire feeder supports and we are concerned that going backwards would lead to tangled wire. If we ever need to go backwards, we just update this schema and the code is already implmented to go backwards in the weld machine.
      direction: zod.enum(['forward']),
    }),
    zod.object({
      kind: zod.literal('stopWireFeed'),
    }),
    zod.object({
      kind: zod.literal('setDryRunMode'),
      isDryRunMode: zod.boolean(),
    }),
    MillerWeldParameters.extend({
      kind: zod.literal('setWeldParameters'),
      weldTravelSpeed: zod.number().gte(0), // Meters/sec.
      // Omitted entirely when millerWeldIntegrationV2 is off, so the sequence
      // registers are left untouched on robots that predate the feature.
      preflow: gasFlowConfig.optional(),
      postflow: gasFlowConfig.optional(),
    }),
  ]),
});

export type MillerWeldMachineCommand = zod.infer<
  typeof MillerWeldMachineCommand
>;
